import {Fragment, useEffect, useRef, useState} from 'react';
import {Alert, Breadcrumb, Button, Center, Dialog, Field, HStack, Input, List, Portal, Spinner, Text} from '@chakra-ui/react';
import {DialogCloseButton} from './ui/dialog-close-button';
import {toaster} from './ui/toaster';
import {useCopyFiles, useDrives, useFileMeta, useFolderChildren, useMoveFiles} from '../drive/queries';
import {DriveFile, isFolder} from '../drive/types';
import {defaultCopyName, parseDriveYear, parseFolderName} from '../domain/naming';
import {describeDriveError} from '../drive/driveApi';
import {PlazmaStars} from '../hooks/usePlazmaStars';
import {PlazmaCopiesOption} from './PlazmaCopiesOption';
import {NoexportBadge} from './NoexportBadge';

export interface MoveFileTrailItem {
    id: string;
    label?: string;
}

interface MoveFileModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Moves the files to the picked folder, or copies them there (also into their own folder). */
    mode?: 'move' | 'copy';
    files: DriveFile[];
    sourceFolderId: string;
    sourceDriveId?: string;
    initialTrail?: MoveFileTrailItem[];
    /** Called once the files have been moved or copied. */
    onDone?: () => void;
    /** Set when moving out of a web bin: the moved photos that also have a plazma copy. Ignored for copies. */
    plazmaStarredFiles?: DriveFile[];
    plazma?: PlazmaStars;
}

const MAX_BREADCRUMB_ITEMS = 5;

function FolderCrumbLabel({crumb}: {crumb: MoveFileTrailItem}) {
    const metaQuery = useFileMeta(crumb.label ? undefined : crumb.id);
    const name = crumb.label ?? metaQuery.data?.name;
    return <>{name !== undefined ? parseFolderName(name).displayName : '…'}</>;
}

export function MoveFileModal({
    isOpen,
    onClose,
    mode = 'move',
    files,
    sourceFolderId,
    sourceDriveId,
    initialTrail,
    onDone,
    plazmaStarredFiles = [],
    plazma,
}: MoveFileModalProps) {
    const isCopy = mode === 'copy';
    const moveFiles = useMoveFiles();
    const copyFiles = useCopyFiles();
    const drivesQuery = useDrives();
    const [driveId, setDriveId] = useState<string | undefined>(undefined);
    const [trail, setTrail] = useState<MoveFileTrailItem[]>([]);
    const [removePlazmaCopies, setRemovePlazmaCopies] = useState(false);
    // A single file's copy can be named; several get their default names.
    const [copyName, setCopyName] = useState('');
    const nameInputRef = useRef<HTMLInputElement>(null);
    // Copying leaves the photos (and so their link to the plazma copies) in place.
    const showPlazmaOption = !isCopy && !!plazma && plazmaStarredFiles.length > 0;
    const plazmaCopiesForbidden = showPlazmaOption && !plazma.canRemoveCopies(plazmaStarredFiles);
    const currentFolderId = trail.length > 0 ? trail[trail.length - 1].id : driveId;
    const childrenQuery = useFolderChildren(driveId, currentFolderId);

    // reset state when modal is closed, or when it is opened with a different source folder
    const wasOpen = useRef(false);
    useEffect(() => {
        if (isOpen && !wasOpen.current) {
            const startingTrail = initialTrail ?? (
                sourceDriveId && sourceFolderId !== sourceDriveId
                    ? [{id: sourceFolderId}]
                    : []
            );
            setDriveId(sourceDriveId);
            setTrail(startingTrail);
            // Default to leaving plazma copies alone.
            setRemovePlazmaCopies(false);
            setCopyName(files.length === 1 ? defaultCopyName(files[0].name) : '');
        }
        wasOpen.current = isOpen;
    }, [files, initialTrail, isOpen, sourceDriveId, sourceFolderId]);

    const years = (drivesQuery.data ?? [])
        .map((drive) => ({drive, year: parseDriveYear(drive.name)}))
        .filter((entry): entry is { drive: typeof entry.drive; year: number } => entry.year !== null)
        .sort((a, b) => b.year - a.year);

    function reset() {
        setDriveId(undefined);
        setTrail([]);
    }

    function handleClose() {
        onClose();
    }

    const folders = (childrenQuery.data ?? []).filter(isFolder);
    const copies = isCopy
        ? files.map((f) => ({fileId: f.id, name: files.length === 1 ? copyName.trim() : defaultCopyName(f.name)}))
        : [];
    // Drive allows several files of the same name in a folder, but they are hard to tell apart.
    const takenNames = new Set((childrenQuery.data ?? []).filter((f) => !isFolder(f)).map((f) => f.name));
    const takenCopyCount = currentFolderId ? copies.filter((c) => takenNames.has(c.name)).length : 0;
    const canSubmit = !!currentFolderId && (isCopy
        ? copies.every((c) => c.name !== '')
        // Moving into the folder the files are already in would do nothing.
        : currentFolderId !== sourceFolderId);

    async function moveTo(targetFolderId: string) {
        // Resolve the copies before moving, while the photos are still listed in the web bin.
        const plazmaCopies = showPlazmaOption && removePlazmaCopies && !plazmaCopiesForbidden
            ? plazma.copiesOf(plazmaStarredFiles)
            : [];
        await moveFiles.mutateAsync({fileIds: files.map((f) => f.id), addParentId: targetFolderId, removeParentId: sourceFolderId});
        await plazma?.removeCopies(plazmaCopies);
    }

    async function handleSubmit() {
        if (!currentFolderId || !canSubmit) return;
        try {
            if (isCopy) {
                await copyFiles.mutateAsync({copies, parentId: currentFolderId});
            } else {
                await moveTo(currentFolderId);
            }
            const done = isCopy ? 'másolva' : 'áthelyezve';
            toaster.create({type: 'success', title: files.length > 1 ? `${files.length} fájl ${done}` : `Fájl ${done}`});
            onDone?.();
            handleClose();
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        }
    }

    const currentDriveName = years.find((y) => y.drive.id === driveId)?.drive.name;

    const breadcrumbItems = [
        {
            key: 'drives',
            label: 'Meghajtók',
            onClick: reset,
        },
        ...(driveId ? [{
            key: driveId,
            label: currentDriveName ? String(parseDriveYear(currentDriveName)) : '…',
            onClick: () => setTrail([]),
        }] : []),
        ...trail.map((crumb, i) => ({
            key: crumb.id,
            label: <FolderCrumbLabel crumb={crumb}/>,
            onClick: () => setTrail(trail.slice(0, i + 1)),
        })),
    ];

    const visibleBreadcrumbItems = breadcrumbItems.length > MAX_BREADCRUMB_ITEMS
        ? [
            ...breadcrumbItems.slice(0, 2),
            {key: 'ellipsis', label: '...', onClick: undefined},
            ...breadcrumbItems.slice(-MAX_BREADCRUMB_ITEMS + 2),
        ]
        : breadcrumbItems;
    visibleBreadcrumbItems[visibleBreadcrumbItems.length - 1].onClick = undefined;  // last item is current folder, not clickable

    return <Dialog.Root
        open={isOpen}
        onOpenChange={(e) => {
            if (!e.open) handleClose();
        }}
        // Reset only once the close animation is over, so the content doesn't jump back meanwhile.
        onExitComplete={reset}
        size="lg"
        scrollBehavior="inside"
        initialFocusEl={isCopy && files.length === 1 ? () => nameInputRef.current : undefined}
    >
        <Portal>
        <Dialog.Backdrop/>
        <Dialog.Positioner>
        <Dialog.Content>
            <Dialog.Header><Dialog.Title>
                {files.length > 1 ? `${files.length} fájl` : 'Fájl'} {isCopy ? 'másolása' : 'áthelyezése'}
            </Dialog.Title></Dialog.Header>
            <DialogCloseButton/>
            <Dialog.Body maxH="60vh" overflowY="auto">
                <Breadcrumb.Root mb={3} fontSize="sm">
                    <Breadcrumb.List gap={2} color="inherit" fontSize="sm" lineHeight="1.5">
                        {visibleBreadcrumbItems.map((item, index) => (
                            <Fragment key={item.key}>
                                {index > 0 && <Breadcrumb.Separator color="inherit">/</Breadcrumb.Separator>}
                                <Breadcrumb.Item>
                                    {item.onClick === undefined
                                        ? <Text>{item.label}</Text>
                                        : <Breadcrumb.Link
                                            onClick={item.onClick}
                                            cursor='pointer'
                                            color="inherit"
                                            _hover={{textDecoration: 'underline', color: 'inherit'}}
                                        >
                                            {item.label}
                                        </Breadcrumb.Link>
                                    }
                                </Breadcrumb.Item>
                            </Fragment>
                        ))}
                    </Breadcrumb.List>
                </Breadcrumb.Root>

                {!driveId
                    ? (drivesQuery.isLoading
                        ? <Center py={6}><Spinner/></Center>
                        : <List.Root variant="plain" gap={1}>
                            {years.map(({drive, year}) => (
                                <List.Item key={drive.id}>
                                    <Button variant="ghost" w="100%" justifyContent="start" onClick={() => setDriveId(drive.id)}>
                                        {year}
                                    </Button>
                                </List.Item>
                            ))}
                        </List.Root>)
                    : childrenQuery.isLoading
                        ? <Center py={6}><Spinner/></Center>
                        : childrenQuery.isError
                            ? <Text color="red.400">{describeDriveError(childrenQuery.error)}</Text>
                            : <List.Root variant="plain" gap={1}>
                                {folders.map((folder) => {
                                    const {displayName, noexport} = parseFolderName(folder.name);
                                    return <List.Item key={folder.id}>
                                        <Button
                                            variant="ghost"
                                            w="100%"
                                            justifyContent="start"
                                            onClick={() => setTrail([...trail, {id: folder.id, label: folder.name}])}
                                        >
                                            {displayName}
                                            {noexport && <NoexportBadge/>}
                                        </Button>
                                    </List.Item>;
                                })}
                                {folders.length === 0 &&
                                    <Text color="gray.500" fontSize="sm">Itt nincsenek almappák.</Text>
                                }
                            </List.Root>
                }
            </Dialog.Body>
            <Dialog.Footer flexDirection="column" alignItems="stretch" gap={3}>
                {isCopy && files.length === 1 &&
                    <Field.Root invalid={copyName.trim() === ''}>
                        <Field.Label>A másolat neve</Field.Label>
                        <Input
                            ref={nameInputRef}
                            value={copyName}
                            onChange={(e) => setCopyName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSubmit();
                            }}
                        />
                    </Field.Root>
                }
                {takenCopyCount > 0 &&
                    <Alert.Root status="warning" borderRadius="md" fontSize="sm">
                        <Alert.Indicator/>
                        <Alert.Title>
                            {files.length === 1
                                ? 'Ebben a mappában már van ilyen nevű fájl.'
                                : `${takenCopyCount} másolat nevével már található fájl ebben a mappában.`}
                        </Alert.Title>
                    </Alert.Root>
                }
                {showPlazmaOption &&
                    <PlazmaCopiesOption
                        fileCount={files.length}
                        starredCount={plazmaStarredFiles.length}
                        action="remove"
                        checked={removePlazmaCopies}
                        onChange={setRemovePlazmaCopies}
                        removeForbidden={plazmaCopiesForbidden}
                    />
                }
                <HStack justify="flex-end">
                    <Button onClick={handleClose} variant="ghost">Mégse</Button>
                    <Button
                        colorPalette="blue"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        loading={isCopy ? copyFiles.isPending : moveFiles.isPending}
                    >
                        {isCopy ? 'Másolás ide' : 'Áthelyezés ide'}
                    </Button>
                </HStack>
            </Dialog.Footer>
        </Dialog.Content>
        </Dialog.Positioner>
        </Portal>
    </Dialog.Root>;
}
